// ============================================================================
// src/dataTypes/liveStreamManagment/sourceRegistry/SourceRegistry.ts
// v2 — disableSource / enableSource délèguent à HlsAcquirer
// ============================================================================

import {
  Source,
  SourceConfig,
  SourceType,
  SourceState,
  SourceEvent,
  SourceEventType,
  SourceEventListener,
  SourceAcquirer,
  BorrowHandle,
} from './types';
import { HlsAcquirer } from './acquirers/HlsAcquirer';

export interface RegisterOptions {
  id: string;
  label: string;
  config: SourceConfig;
}

export class SourceRegistry {

  private sources = new Map<string, Source>();
  private acquirers = new Map<SourceType, SourceAcquirer>();
  private listeners = new Set<SourceEventListener>();
  private typeListeners = new Map<SourceEventType, Set<SourceEventListener>>();

  registerAcquirer(type: SourceType, acquirer: SourceAcquirer): void {
    this.acquirers.set(type, acquirer);
  }

  private getAcquirer(type: SourceType): SourceAcquirer {
    const acquirer = this.acquirers.get(type);
    if (!acquirer) {
      throw new Error(`[SourceRegistry] Aucun acquirer enregistré pour le type "${type}"`);
    }
    return acquirer;
  }

  subscribe(listener: SourceEventListener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  on(type: SourceEventType, listener: SourceEventListener): () => void {
    if (!this.typeListeners.has(type)) {
      this.typeListeners.set(type, new Set());
    }
    this.typeListeners.get(type)!.add(listener);
    return () => { this.typeListeners.get(type)?.delete(listener); };
  }

  private emit(event: SourceEvent): void {
    this.listeners.forEach(fn => {
      try { fn(event); } catch (e) { console.error('[SourceRegistry] Listener error:', e); }
    });
    this.typeListeners.get(event.type)?.forEach(fn => {
      try { fn(event); } catch (e) { console.error('[SourceRegistry] TypeListener error:', e); }
    });
  }

  register(options: RegisterOptions): Source {

    console.log('SourceRegistry::register', options);

    const existing = this.sources.get(options.id);
    if (existing) {
      const labelChanged  = existing.label !== options.label;
      const configChanged = existing.config !== options.config;
      existing.label  = options.label;
      existing.config = options.config;
      if (labelChanged || configChanged) {
        this.emit({ type: 'state-changed', sourceId: existing.id, source: existing });
      }
      return existing;
    }

    const source: Source = {
      id:       options.id,
      type:     options.config.type,
      label:    options.label,
      config:   options.config,
      state:    'idle',
      stream:   null,
      refCount: 0,
    };

    this.sources.set(options.id, source);
    this.emit({ type: 'registered', sourceId: source.id, source });
    return source;
  }

  unregister(sourceId: string): void {
    const source = this.sources.get(sourceId);
    if (!source) return;

    if (source.state === 'active' || source.state === 'acquiring') {
      this.forceRelease(source);
    }

    this.sources.delete(sourceId);
    this.emit({ type: 'unregistered', sourceId, source });
  }

  get(sourceId: string): Source | undefined {
    return this.sources.get(sourceId);
  }

  getStream(sourceId: string): MediaStream | null {
    return this.sources.get(sourceId)?.stream ?? null;
  }

  getState(sourceId: string): SourceState {
    return this.sources.get(sourceId)?.state ?? 'idle';
  }

  getAll(): Source[] {
    return Array.from(this.sources.values());
  }

  getAllByType(type: SourceType): Source[] {
    return this.getAll().filter(s => s.type === type);
  }

  getActive(): Source[] {
    return this.getAll().filter(s => s.state === 'active');
  }

  // `MediaStream.active` reste `true` tant qu'IL SUFFIT D'UNE SEULE piste vivante — un flux
  // webcam audio+vidéo dont seule la vidéo s'est terminée (piste "ended") passe donc `.active`
  // haut la main alors que l'image, elle, est bien morte. Constaté le 15/09/2026 sur Lovelight
  // TV : `active=true`, `video:ended` + `audio:live` dans le même flux. On exige donc que
  // TOUTES les pistes soient encore "live", pas seulement qu'il en reste une.
  private _isStreamHealthy(stream: MediaStream | null): stream is MediaStream {
    return !!stream && stream.getTracks().every(t => t.readyState === 'live');
  }

  private async acquireInternal(sourceId: string): Promise<MediaStream> {
    const source = this.sources.get(sourceId);
    if (!source) {
      throw new Error(`[SourceRegistry] Source "${sourceId}" non enregistrée`);
    }

    // Voir _isStreamHealthy : un flux dont une SEULE piste est morte (typiquement la vidéo,
    // l'audio restant "live") reste un objet valide et non-null, `.active` y compris, mais
    // mort pour l'usage qu'on en fait. Sans ce test, une source dont la piste s'est arrêtée
    // pour une raison qui n'est jamais passée par release()/doRelease() (donc jamais repassée
    // à 'idle') restait déclarée 'active' pour toujours : ce même flux zombie était reservi à
    // chaque nouvel emprunt, chaîne après chaîne, sans jamais rouvrir la caméra.
    if (source.state === 'active' && this._isStreamHealthy(source.stream)) {
      return source.stream;
    }

    if (source.state === 'acquiring') {
      return this.waitForActive(sourceId);
    }

    const acquirer = this.getAcquirer(source.type);
    this.setState(source, 'acquiring');

    try {
      const stream = await acquirer.acquire(source);
      source.stream = stream;

      console.log(`[Registry.acquireInternal] ${sourceId} stream stocké:`,
        stream.getTracks().map((t: MediaStreamTrack) => `${t.kind}:${t.id.slice(0,8)}:${t.label}`)
      );

      source.error = undefined;
      this.setState(source, 'active');
      this.emit({ type: 'stream-updated', sourceId, source });
      return stream;
    } catch (err: any) {
      source.error = err.message ?? String(err);
      source.stream = null;
      this.setState(source, 'error');
      this.emit({ type: 'error', sourceId, source, detail: err.message });
      throw err;
    }
  }

  release(sourceId: string): void {
    const source = this.sources.get(sourceId);
    if (!source) return;

    if (source.refCount > 0) {
      source.refCount--;
    }

    if (source.refCount <= 0) {
      this.doRelease(source);
    }
  }

  forceRelease(sourceId: string): void;
  forceRelease(source: Source): void;
  forceRelease(sourceOrId: string | Source): void {
    const source = typeof sourceOrId === 'string'
      ? this.sources.get(sourceOrId)
      : sourceOrId;
    if (!source) return;
    source.refCount = 0;
    this.doRelease(source);
  }

  private doRelease(source: Source): void {
    if (source.state !== 'active' && source.state !== 'acquiring' && source.state !== 'error') {
      return;
    }

    const hlsAcquirer = this.acquirers.get('hls') as HlsAcquirer | undefined;
    if (hlsAcquirer) {
      const hlsEntry = hlsAcquirer.entries.get(source.id);
if (hlsEntry?.isRetrying) {
  console.log(`[SourceRegistry] doRelease ignoré pour ${source.id} — renouvellement ou retry en cours`);
  return;
}
    }

    try {
      const acquirer = this.acquirers.get(source.type);
      acquirer?.release(source);
    } catch (e) {
      console.warn(`[SourceRegistry] Erreur release ${source.id}:`, e);
    }

    source.stream   = null;
    source.refCount = 0;
    source.error    = undefined;
    this.bumpGeneration(source.id);
    this.setState(source, 'idle');
  }

  // Voir le commentaire de `borrow` : un compteur par source, incrémenté à chaque remise à zéro.
  private generations = new Map<string, number>();
  private generationOf(sourceId: string): number { return this.generations.get(sourceId) ?? 0; }
  private bumpGeneration(sourceId: string): void { this.generations.set(sourceId, this.generationOf(sourceId) + 1); }

  async borrow(sourceId: string): Promise<BorrowHandle> {
    const source = this.sources.get(sourceId);
    if (!source) {
      throw new Error(`[SourceRegistry] Source "${sourceId}" non enregistrée`);
    }

    // La CAPTURE compte : `acquireInternal` retourne le flux qu'IL vient d'obtenir, avant que
    // quiconque n'ait pu y toucher. Relire `source.stream` après coup — comme le faisait cette
    // fonction jusqu'au 15/09/2026 — expose un champ partagé et mutable : l'auto-emprunt des
    // mini-vues (cf. l'abonnement 'stream-updated' de useLiveStreamSetup) réagit à CE MÊME
    // évènement, émis PENDANT acquireInternal, donc avant que cette fonction n'ait repris la
    // main — et peut avoir déjà relâché la source (`source.stream = null`) au moment de la
    // relecture. Ça plantait avec "Cannot read properties of null (reading 'id')" (constaté sur
    // Lovelight TV, webcam locale, en changeant de chaîne). En utilisant la valeur retournue,
    // la course n'a plus prise : peu importe ce qu'un autre emprunteur fait de `source.stream`
    // ensuite, le flux qu'on vient d'obtenir reste le même objet, valide.
    // _isStreamHealthy, pas seulement `source.stream?.active` — cf. son commentaire dans
    // acquireInternal() : `.active` seul rate le cas d'une SEULE piste morte (vidéo "ended",
    // audio toujours "live" dans le même flux), constaté sur ce point précis le 15/09/2026.
    let stream = (source.state === 'active' && this._isStreamHealthy(source.stream))
      ? source.stream
      : await this.acquireInternal(sourceId);

    // Dernier rempart : le flux qu'on s'apprête à rendre peut être mort ALORS QUE le registre
    // vient de le juger sain (ou de l'obtenir lui-même). Constaté le 20/09/2026 entre 16h38 et
    // 16h41, en enchaînant les régies : `borrow` rendait un flux webcam `active=false` /
    // `readyState=ended` — 5 fois en 3 minutes, toujours dans la foulée d'un « [MINIVUE] 🔄 flux
    // remplacé », donc quand un second flux pour la même caméra était apparu entre-temps.
    // L'appelant, lui, n'avait aucun moyen de le savoir : il basculait « quand même » sur une
    // source noire (timeout de 10 s de `startCanvasForStream`). Ici on rouvre la caméra une fois ;
    // si elle reste morte on lève, ce qui laisse la source précédente à l'antenne (`giveUp`).
    // Limité aux caméras locales : rouvrir un écran repose une question à l'opérateur, et une
    // source distante ne se rouvre pas d'elle-même.
    if (source.type === 'device' && !this._isStreamHealthy(stream)) {
      stream = await this.reacquireDeadDeviceStream(source, stream);
    }

    source.refCount++;

    console.log(`[Registry.borrow] ${sourceId} → stream id: ${stream.id}, tracks:`,
      stream.getTracks().map((t: MediaStreamTrack) => `${t.kind}:${t.id.slice(0,8)}:${t.label}`)
    );

    // Génération de la source au moment de l'emprunt : `doRelease` la remet à zéro (refCount=0,
    // pistes coupées) SANS que les emprunteurs déjà en place le sachent. Un `release()` tardif de
    // l'un d'eux décrémentait alors le compte du flux FRAIS d'un autre emprunteur — jusqu'à zéro,
    // donc `doRelease` — et coupait la caméra qu'on venait d'ouvrir. Constaté le 20/09/2026 à 21h07 :
    // 4 coupures d'affilée (`arrêt des pistes … refCount=0`), chacune dans la seconde qui suivait
    // une réouverture, jusqu'à « Impossible de démarrer » (cf. reacquireDeadDeviceStream).
    // Un emprunt d'une génération révolue ne compte plus pour rien : son décompte a déjà été
    // effacé avec le reste.
    const generation = this.generationOf(sourceId);

    let released = false;
    return {
      sourceId,
      stream,
      release: () => {
        if (released) return;
        released = true;
        if (this.generationOf(sourceId) !== generation) return;
        this.release(sourceId);
      },
    };
  }

  /**
   * Rouvre une caméra locale dont `borrow` s'apprêtait à rendre un flux mort.
   *
   * `refCount` n'est volontairement PAS touché : les emprunteurs déjà en place (mini-vue,
   * antenne) restent comptés, et leurs `release()` ultérieurs — notamment celui de
   * l'ancienne mini-vue quand elle emprunte le nouveau flux — doivent retomber juste.
   * Passer par `doRelease` remettrait le compte à zéro et ferait relâcher, par ces mêmes
   * `release()`, le flux frais qu'on vient d'ouvrir.
   */
  private async reacquireDeadDeviceStream(source: Source, dead: MediaStream): Promise<MediaStream> {
    const etat = dead.getTracks().map(t => `${t.kind}:${t.readyState}`).join(',');
    console.warn(`[Registry.borrow] ${source.id} → flux rendu déjà mort (${etat}, état=${source.state}, refCount=${source.refCount}) — réacquisition`);

    // Trois essais espacés, comme `DeviceAcquirer` : lors d'un changement de chaîne, le registre de
    // la chaîne précédente (ou celui, transitoire, monté avec `channelUuid=null`) coupe ses pistes
    // dans la même seconde que la suivante rouvre la caméra — un second essai immédiat pourrait
    // tomber pile dans cette fenêtre.
    const DELAIS_MS = [0, 300, 700];
    let courant = dead;

    for (let essai = 0; essai < DELAIS_MS.length; essai++) {
      if (DELAIS_MS[essai] > 0) await new Promise(r => setTimeout(r, DELAIS_MS[essai]));

      // Si le registre pointe encore sur ce flux mort, on le décroche pour que `acquireInternal`
      // rouvre la caméra ; s'il pointe déjà sur un autre flux, `acquireInternal` le rendra tel
      // quel dès qu'il est sain.
      if (source.stream === courant) {
        try { this.acquirers.get(source.type)?.release(source); } catch { /* best-effort */ }
        source.stream = null;
        this.setState(source, 'idle');
      }

      const fresh = await this.acquireInternal(source.id);
      if (this._isStreamHealthy(fresh)) {
        if (essai > 0) console.warn(`[Registry.borrow] ${source.id} : caméra rouverte à la tentative ${essai + 1}`);
        return fresh;
      }
      courant = fresh;
    }

    throw new Error(`[SourceRegistry] ${source.id} : flux webcam encore mort après ${DELAIS_MS.length} réacquisitions`);
  }

  /**
   * Signale qu'une source active vient de mourir de son propre chef — piste
   * arrêtée par le navigateur, périphérique débranché, permission révoquée.
   *
   * Volontairement, on ne libère PAS la source ici. `doRelease` la remettrait à
   * `idle`, l'auto-borrow du deck la réacquerrait dans la foulée, et si la panne
   * est durable (caméra réellement partie) l'échec d'acquisition émettrait à
   * nouveau — une boucle serrée de getUserMedia, du genre qui fige la régie.
   * L'état `error` est un point d'arrêt : c'est l'opérateur qui relance, par le
   * bouton de réacquisition de la fenêtre de réglage.
   */
  reportFailure(sourceId: string, reason: string): void {
    const source = this.sources.get(sourceId);
    if (!source) return;
    if (source.state === 'error') return;   // déjà signalé — une piste par flux, mais deux flux par source possible

    source.error = reason;
    this.setState(source, 'error');
    this.emit({ type: 'error', sourceId, source, detail: reason });
  }

  // ── disableSource / enableSource ──────────────────────────────────────────
  // Délèguent à HlsAcquirer pour bloquer acquire() et stopper hls.js.
  // Utilisé pour le power OFF/ON d'une source url dans le deck.

  disableSource(sourceId: string): void {
    const hlsAcquirer = this.acquirers.get('hls') as HlsAcquirer | undefined;
    hlsAcquirer?.disableSource(sourceId);
  }

  enableSource(sourceId: string): void {
    const hlsAcquirer = this.acquirers.get('hls') as HlsAcquirer | undefined;
    hlsAcquirer?.enableSource(sourceId);
  }

  injectStream(sourceId: string, stream: MediaStream): void {
    const source = this.sources.get(sourceId);
    if (!source) {
      console.warn(`[SourceRegistry] injectStream: source "${sourceId}" non trouvée`);
      return;
    }

    if (source.stream && source.stream !== stream) {
      try {
        const acquirer = this.acquirers.get(source.type);
        acquirer?.release(source);
      } catch { /* best-effort */ }
    }

    source.stream   = stream;
    source.error    = undefined;
    if (source.refCount === 0) source.refCount = 1;
    this.setState(source, 'active');
    setTimeout(() => {
      this.emit({ type: 'stream-updated', sourceId, source });
    }, 0);
  }

  ejectStream(sourceId: string): void {
    const source = this.sources.get(sourceId);
    if (!source) return;

    source.stream   = null;
    source.refCount = 0;
    source.error    = undefined;
    this.bumpGeneration(sourceId);
    this.setState(source, 'idle');
  }

  replaceTrack(sourceId: string, newTrack: MediaStreamTrack): void {
    const source = this.sources.get(sourceId);
    if (!source?.stream) return;

    const oldTrack = source.stream.getTracks().find(t => t.kind === newTrack.kind);
    if (oldTrack) {
      source.stream.removeTrack(oldTrack);
      oldTrack.stop();
    }
    source.stream.addTrack(newTrack);
    this.emit({ type: 'stream-updated', sourceId, source });
  }

  private setState(source: Source, state: SourceState): void {
    const prev = source.state;
    if (prev === state) return;
    source.state = state;
    this.emit({
      type: 'state-changed',
      sourceId: source.id,
      source,
      detail: { previousState: prev },
    });
  }

  private waitForActive(sourceId: string): Promise<MediaStream> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        unsub();
        reject(new Error(`[SourceRegistry] Timeout en attendant l'acquisition de "${sourceId}"`));
      }, 30_000);

      const unsub = this.subscribe(event => {
        if (event.sourceId !== sourceId) return;
        if (event.type === 'state-changed') {
          if (event.source.state === 'active' && event.source.stream) {
            clearTimeout(timeout);
            unsub();
            resolve(event.source.stream);
          } else if (event.source.state === 'error') {
            clearTimeout(timeout);
            unsub();
            reject(new Error(event.source.error ?? 'Acquisition échouée'));
          }
        }
      });
    });
  }

  destroy(): void {
    this.sources.forEach(source => {
      this.forceRelease(source);
    });
    this.sources.clear();
    this.listeners.clear();
    this.typeListeners.clear();
  }
}